import { useState } from "react";

import { OrderTicketDrawer } from "../orderTicketDrawer/OrderTicketDrawer.connector";
import {
  OrderTicketDrawerProvider,
  useOrderTicketDrawer,
  useOrderTicketDrawerState,
} from "../orderTicketDrawer/OrderTicketDrawer.context";
import { WorkspaceGroupSelect } from "../shared/groupSelect/WorkspaceGroupSelect.connector";
import { LayoutToolbarPresentation } from "../shared/LayoutToolbar.pres";
import { LayoutSettingsPanelPresentation } from "../shared/layoutSettings/LayoutSettingsPanel.pres";
import { WorkspaceWidget } from "../shared/WorkspaceWidget.connector";
import { WorkspaceProvider } from "../shared/workspace/Workspace.context";
import { useReactGridLayoutTabController } from "./ReactGridLayoutTab.controller";
import { ReactGridLayoutTabPresentation } from "./ReactGridLayoutTab.pres";
import type { RglSettingId } from "./settings/reactGridLayoutSettings.schema";

export interface ReactGridLayoutTabProps {
  workspaceId: string;
}

/**
 * Connected tab without its own provider.
 * Exported so integration tests can supply the workspace provider themselves
 * and observe the store the tab is writing to.
 */
export const ReactGridLayoutTabConnected = () => {
  const controller = useReactGridLayoutTabController();
  const { toggleOrderTicket } = useOrderTicketDrawer();
  const drawerState = useOrderTicketDrawerState();
  // Element state (not a ref) so the drawer re-renders with its portal
  // container as soon as the slot mounts.
  const [drawerSlot, setDrawerSlot] = useState<HTMLDivElement | null>(null);

  const settingsPanel = (
    <LayoutSettingsPanelPresentation
      expanded={controller.settings.expanded}
      panelId={controller.settings.panelId}
      labelledBy={controller.settings.buttonId}
      sections={controller.settings.sections}
      onFieldChange={(fieldId, change) =>
        controller.settings.onFieldChange(fieldId as RglSettingId, change)
      }
    />
  );

  const toolbar = (
    <LayoutToolbarPresentation
      onAddWidget={controller.onAddWidget}
      onToggleOrderTicket={toggleOrderTicket}
      orderTicketOpen={drawerState.open}
      settings={{
        expanded: controller.settings.expanded,
        buttonId: controller.settings.buttonId,
        panelId: controller.settings.panelId,
        onToggle: controller.settings.onToggleExpanded,
        panel: settingsPanel,
      }}
    />
  );

  return (
    <ReactGridLayoutTabPresentation
      width={controller.width}
      mounted={controller.mounted}
      containerRef={controller.containerRef}
      layout={controller.layout}
      onLayoutChange={controller.onLayoutChange}
      appliedSettings={controller.appliedSettings}
      toolbar={toolbar}
      drawerReservedWidth={drawerState.reservedWidth}
      drawerTransition={drawerState.transition}
      drawerSlotRef={setDrawerSlot}
      drawer={<OrderTicketDrawer container={drawerSlot} />}
      renderWidget={(id) => <WorkspaceWidget id={id} />}
      workspaceGroupSelect={<WorkspaceGroupSelect />}
    />
  );
};

/**
 * Scoped provider for the RGL tab. Creates one workspace store per mount;
 * hydrates from / persists to the matching entry in the layout document.
 */
export const ReactGridLayoutTab = ({
  workspaceId,
}: ReactGridLayoutTabProps) => (
  <WorkspaceProvider persist workspaceId={workspaceId}>
    <OrderTicketDrawerProvider engine="rgl">
      <ReactGridLayoutTabConnected />
    </OrderTicketDrawerProvider>
  </WorkspaceProvider>
);
